import Pagination from '../../components/Common/Pagination';
import { TableLoader, Spinner } from '../../components/Common/Loader';
import React, { useState, useMemo, useEffect } from 'react';
import { ArrowLeft } from 'lucide-react';
import { getSizes, createSize, updateSize, getUnits } from '../../api/productMasters';
import CSVButton from '../../components/Common/CSVButton';
import { exportToCSV } from '../../utils/csvExport';

interface SizeItem {
  id: number;
  unitId?: number | string;
  unit: string;
  sizeValue: string;
  erp: string;
  sequenceNo: string;
  status: 'Active' | 'Inactive';
}

interface UnitOption {
  id: number;
  name: string;
  symbol: string;
}

export const SizesList: React.FC = () => {
  const [sizes, setSizes] = useState<SizeItem[]>([]);
  const [availableUnits, setAvailableUnits] = useState<UnitOption[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [view, setView] = useState<'list' | 'add' | 'edit'>('list');
  const [editingSize, setEditingSize] = useState<SizeItem | null>(null);

  // Fetch Sizes & Units from API
  const fetchSizesData = async () => {
    try {
      setLoading(true);
      const res = await getSizes();
      if (res && (res.status === 'success' || res.success) && res.data) {
        const rawList = Array.isArray(res.data)
          ? res.data
          : ((res.data as any)?.data || (res.data as any)?.sizes || []);

        if (Array.isArray(rawList)) {
          const mapped: SizeItem[] = rawList.map((item: any) => ({
            id: item.id,
            unitId: item.unitId || item.unit?.id,
            unit: item.unit?.symbol || item.unit?.name || String(item.unitId || '1'),
            sizeValue: String(item.sizeValue ?? '1'),
            erp: item.sapCode || (item.unit?.symbol ? `${item.sizeValue} ${item.unit.symbol}` : '-'),
            sequenceNo: String(item.id),
            status: (item.status === 1 || item.status === '1' || item.status === 'Active') ? 'Active' : 'Inactive'
          }));
          setSizes(mapped);
        }
      }
    } catch (err) {
      console.error('Error fetching sizes list:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchUnitsData = async () => {
    try {
      const res = await getUnits();
      if (res && (res.status === 'success' || res.success) && res.data) {
        const rawList = Array.isArray(res.data) ? res.data : (res.data as any)?.data || [];
        if (Array.isArray(rawList)) {
          setAvailableUnits(rawList.map((u: any) => ({
            id: u.id,
            name: u.name || '',
            symbol: u.symbol || ''
          })));
        }
      }
    } catch (err) {
      console.error('Error fetching units for dropdown:', err);
    }
  };

  useEffect(() => {
    fetchSizesData();
    fetchUnitsData();
  }, []);

  const [activeTab, setActiveTab] = useState<'Active' | 'Inactive' | 'All'>('All');
  const [searchQuery, setSearchQuery] = useState('');
  const [entriesLimit, setEntriesLimit] = useState(25);
  const [currentPage, setCurrentPage] = useState(1);

  // Form State
  const [formData, setFormData] = useState({
    unit: '',
    sizeValue: '',
    erp: '',
    sequenceNo: '',
    status: 'Active' as 'Active' | 'Inactive',
  });

  // Dynamic active/inactive counts
  const activeCounts = useMemo(() => sizes.filter(s => s.status === 'Active').length, [sizes]);
  const inactiveCounts = useMemo(() => sizes.filter(s => s.status === 'Inactive').length, [sizes]);

  // Filtering Logic
  const filteredSizes = useMemo(() => {
    return sizes.filter((size) => {
      if (activeTab === 'Active' && size.status !== 'Active') return false;
      if (activeTab === 'Inactive' && size.status !== 'Inactive') return false;

      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        return (
          size.unit.toLowerCase().includes(query) ||
          size.sizeValue.toLowerCase().includes(query) ||
          size.erp.toLowerCase().includes(query)
        );
      }
      return true;
    });
  }, [sizes, activeTab, searchQuery]);

  // Paginated Sizes
  const paginatedSizes = useMemo(() => {
    const startIndex = (currentPage - 1) * entriesLimit;
    return filteredSizes.slice(startIndex, startIndex + entriesLimit);
  }, [filteredSizes, currentPage, entriesLimit]);

  const handleOpenAdd = () => {
    setEditingSize(null);
    setFormData({ unit: '', sizeValue: '', erp: '', sequenceNo: '', status: 'Active' });
    setView('add');
  };

  const handleOpenEdit = (size: SizeItem) => {
    setEditingSize(size);
    setFormData({
      unit: String(size.unitId || size.unit || ''),
      sizeValue: size.sizeValue,
      erp: size.erp === '-' ? '' : size.erp,
      sequenceNo: size.sequenceNo,
      status: size.status || 'Active',
    });
    setView('edit');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.unit) {
      alert('Unit is required.');
      return;
    }

    try {
      setSubmitting(true);
      const parsedUnitId = Number(formData.unit);
      const parsedSizeVal = Number(formData.sizeValue);

      const payload = {
        unitId: isNaN(parsedUnitId) ? formData.unit : parsedUnitId,
        sizeValue: isNaN(parsedSizeVal) ? formData.sizeValue : parsedSizeVal,
        sapCode: formData.erp || undefined,
        status: formData.status === 'Active' ? 1 : 0,
      };

      if (editingSize) {
        // Update Size: PUT /api/v1/erp/products/sizes/:id
        await updateSize(editingSize.id, payload);
      } else {
        // Create Size: POST /api/v1/erp/products/sizes
        await createSize(payload);
      }

      await fetchSizesData();
      setActiveTab('All');
      setView('list');
    } catch (err: any) {
      console.error('Error saving size:', err);
      alert(err.message || 'Failed to save size. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (view === 'add' || view === 'edit') {
    return (
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
          <div className="flex items-center gap-3">
            <button 
              type="button"
              onClick={() => setView('list')}
              className="w-8 h-8 rounded-full bg-[#C1272D] hover:bg-[#A81E23] transition-all flex items-center justify-center text-white cursor-pointer shadow-sm"
              title="Go Back"
            >
              <ArrowLeft className="w-4 h-4" />
            </button>
            <h1 className="text-2xl font-extrabold text-[#C1272D] tracking-tight">
              {view === 'edit' ? 'Edit Size' : 'Add Size'}
            </h1>
          </div>
        </div>

        {/* Form Container */}
        <form onSubmit={handleSubmit} className="space-y-6 bg-white p-6 rounded-2xl">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-5">
            {/* Unit Dropdown */}
            <div>
              <label className="block text-slate-800 font-bold text-xs mb-1.5">Unit*</label>
              <div className="relative">
                <select
                  required
                  value={formData.unit}
                  onChange={(e) => setFormData({...formData, unit: e.target.value})}
                  className="w-full border border-[#828282] rounded-2xl py-2.5 px-4 outline-none focus:go_erp-border text-xs font-semibold text-slate-700 appearance-none bg-[#F5F5F5]"
                >
                  <option value="">Please Select</option>
                  {availableUnits.length > 0 ? (
                    availableUnits.map((u) => (
                      <option key={u.id} value={u.id}>
                        {u.name} {u.symbol ? `(${u.symbol})` : ''}
                      </option>
                    ))
                  ) : (
                    <>
                      <option value="1">Gram (GM)</option>
                      <option value="2">Kilogram (KG)</option>
                      <option value="3">Liter (LTR)</option>
                      <option value="4">Milliliter (ML)</option>
                    </>
                  )}
                </select>
                <span className="absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none text-slate-500">
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                  </svg>
                </span>
              </div>
            </div>

            {/* Size Value */}
            <div>
              <label className="block text-slate-800 font-bold text-xs mb-1.5">Size Value</label>
              <input 
                type="text" 
                value={formData.sizeValue}
                onChange={(e) => setFormData({...formData, sizeValue: e.target.value})}
                className="w-full border border-[#828282] rounded-2xl py-2.5 px-4 outline-none focus:go_erp-border text-xs font-semibold text-slate-800" 
              />
            </div>

            {/* ERP/SAP ID */}
            <div>
              <label className="block text-slate-800 font-bold text-xs mb-1.5">ERP/SAP ID</label>
              <input 
                type="text" 
                value={formData.erp}
                onChange={(e) => setFormData({...formData, erp: e.target.value})}
                className="w-full border border-[#828282] rounded-2xl py-2.5 px-4 outline-none focus:go_erp-border text-xs font-semibold text-slate-800" 
              />
            </div>

            {/* Status Dropdown */}
            <div>
              <label className="block text-slate-800 font-bold text-xs mb-1.5">Status</label>
              <div className="relative">
                <select
                  value={formData.status}
                  onChange={(e) => setFormData({ ...formData, status: e.target.value as 'Active' | 'Inactive' })}
                  className="w-full bg-[#F5F5F5] border border-[#828282] rounded-2xl py-2.5 px-4 outline-none focus:go_erp-border text-xs font-semibold text-slate-800 appearance-none cursor-pointer"
                >
                  <option value="Active">Active</option>
                  <option value="Inactive">Inactive</option>
                </select>
                <span className="absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none text-slate-500">
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                  </svg>
                </span>
              </div>
            </div>
          </div>

          {/* Submit Button */}
          <div className="flex justify-center pt-4">
            <button 
              type="submit" 
              disabled={submitting}
              className="bg-[#DCA10D] hover:bg-[#c28c00] text-white font-bold py-3 px-16 rounded-xl text-sm transition-all cursor-pointer shadow-md hover:shadow-[#DCA10D]/20 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {submitting && <Spinner size="sm" color="text-white" />}
              {submitting ? 'Saving...' : 'Submit'}
            </button>
          </div>
        </form>
      </div>
    );
  }

  const handleExportCSV = () => {
    const headers = ['ID', 'Unit', 'Size Value', 'ERP Code', 'Sequence No', 'Status'];
    const rows = filteredSizes.map((item) => [
      item.id,
      item.unit,
      item.sizeValue,
      item.erp,
      item.sequenceNo,
      item.status,
    ]);
    exportToCSV(`sizes_${activeTab.toLowerCase()}`, headers, rows);
  };

  return (
    <div className="space-y-6">
      {/* Control Row */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
        {/* Title and Back */}
        <div className="flex items-center gap-3">
          <button 
            onClick={() => window.history.back()}
            className="w-8 h-8 rounded-full bg-[#C1272D] hover:bg-[#A81E23] transition-all flex items-center justify-center text-white cursor-pointer shadow-sm"
            title="Go Back"
          >
            <ArrowLeft className="w-4 h-4" />
          </button>
          <h1 className="text-2xl font-extrabold text-[#C1272D] tracking-tight">
            Sizes
          </h1>
        </div>

        {/* Status Tabs */}
        <div className="flex bg-white p-1 rounded-full border border-slate-200 w-fit">
          {(['All', 'Active', 'Inactive'] as const).map((tab) => {
            const isActive = activeTab === tab;
            return (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`px-6 py-1.5 rounded-full text-xs font-bold transition-all duration-150 cursor-pointer ${
                  isActive
                    ? 'go_erp-bg text-white shadow-sm'
                    : 'text-slate-655 hover:text-slate-900'
                }`}
              >
                {tab}
              </button>
            );
          })}
        </div>

        {/* Action Buttons */}
        <div className="flex items-center gap-3">
          <button
            onClick={handleOpenAdd}
            className="go_erp-bg text-white text-sm px-4 py-1.5 rounded-xl flex items-center gap-2 cursor-pointer transition-colors"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
            </svg>
            Add Size
          </button>
          
          <CSVButton onClick={handleExportCSV} />
        </div>
      </div>

      {/* Counts */}
      <div className="flex items-center justify-end gap-4 text-xs font-bold text-slate-800">
        <span>Active Counts: <span className="font-extrabold">{activeCounts}</span></span>
        <span>Inactive Counts: <span className="font-extrabold">{inactiveCounts}</span></span>
      </div>

      {/* Table Main Card */}
      <div className="bg-white rounded-2xl overflow-hidden">
        
        {/* Table Toolbar */}
        <div className="px-6 py-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 bg-slate-50/50">
          <div className="flex items-center gap-2">
            <span className="text-xs font-semibold text-slate-500">Show</span>
            <select
              value={entriesLimit}
              onChange={(e) => setEntriesLimit(Number(e.target.value))}
              className="bg-white border border-slate-200 text-xs font-bold text-slate-700 rounded px-2 py-1 outline-none focus:border-slate-400"
            >
              <option value={5}>5</option>
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
            </select>
            <span className="text-xs font-semibold text-slate-500">entries</span>
          </div>

          <div className="flex flex-wrap items-center gap-4">
            {/* Table Search */}
            <div className="relative">
              <input
                type="text"
                placeholder="Search"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="bg-white border border-slate-200 text-xs rounded-full py-1.5 pl-8 pr-3 outline-none focus:border-slate-400 w-44 transition-all"
              />
              <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400">
                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </span>
            </div>
          </div>
        </div>

        {/* Table Wrapper */}
        <div className="overflow-x-auto select-none">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="bg-[#FFF8E1]">
                <th className="px-4 py-4 text-xs font-semibold text-[#1f1f1f]">ID</th>
                <th className="px-4 py-4 text-xs font-semibold text-[#1f1f1f]">Unit</th>
                <th className="px-4 py-4 text-xs font-semibold text-[#1f1f1f]">Size Value</th>
                <th className="px-4 py-4 text-xs font-semibold text-[#1f1f1f]">Status</th>
                <th className="px-4 py-4 text-xs font-semibold text-[#1f1f1f] text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#ECECF2]">
              {loading ? (
                <TableLoader colSpan={5} text="Loading..." />
              ) : paginatedSizes.length > 0 ? (
                paginatedSizes.map((sizeItem) => (
                  <tr key={sizeItem.id} className="hover:bg-slate-50/50 transition-colors">
                    <td className="px-4 py-4 text-xs text-[#828282]">{sizeItem.id}</td>
                    <td className="px-4 py-4 text-xs text-[#828282]">{sizeItem.unit}</td>
                    <td className="px-4 py-4 text-xs text-[#828282]">{sizeItem.sizeValue}</td>
                    <td className="px-4 py-4 text-xs text-[#828282]">{sizeItem.status}</td>
                    <td className="px-4 py-4 text-xs text-right">
                        <div className="flex items-center justify-end gap-1">
                          <button onClick={() => handleOpenEdit(sizeItem)} className="p-1.5 text-slate-500 hover:text-[#B71C1C] transition-colors cursor-pointer rounded-lg" title="View">
                            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"/><path strokeLinecap="round" strokeLinejoin="round" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"/></svg>
                          </button>
                          <button onClick={() => handleOpenEdit(sizeItem)} className="p-1.5 text-slate-500 hover:text-blue-600 transition-colors cursor-pointer rounded-lg" title="Edit">
                            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"/></svg>
                          </button>
                        </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={5} className="px-6 py-10 text-center text-xs font-semibold text-slate-400">
                    No sizes found matching filters.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Footer */}
        <Pagination
          currentPage={currentPage}
          totalEntries={filteredSizes.length}
          entriesLimit={entriesLimit}
          onPageChange={setCurrentPage}
        />

      </div>
    </div>
  );
};
export default SizesList;
